import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, addDoc, serverTimestamp, where, deleteDoc, doc } from 'firebase/firestore';

// --- Cloudinary Config ---
const CLOUD_NAME = "invoice-app";       
const API_KEY = "149469791857382";      
const API_SECRET = "CQJwV5ujAkgYO5BHYKMkGIITbMU"; 

const Rates = () => {
  // --- States ---
  const [bookingService, setBookingService] = useState('DHL');
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
  const [bookingFile, setBookingFile] = useState(null);
  const [bookingUploading, setBookingUploading] = useState(false);
  const [bookingFilesList, setBookingFilesList] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [partyService, setPartyService] = useState('DHL');
  const [partyDate, setPartyDate] = useState(new Date().toISOString().split('T')[0]);
  const [partyFile, setPartyFile] = useState(null);
  const [partyUploading, setPartyUploading] = useState(false);
  const [partyFilesList, setPartyFilesList] = useState([]);
  
  const [message, setMessage] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  // --- Initial Load ---
  useEffect(() => {
    fetchCompanies();
    fetchBookingFiles(); 
  }, []);

  useEffect(() => {
    if (selectedCompany) {
        fetchPartyFiles(selectedCompany);
    } else {
        setPartyFilesList([]);
    }
  }, [selectedCompany]);

  // --- Data Fetching ---
  const fetchCompanies = async () => {
    try {
        const q = query(collection(db, 'companies')); 
        const querySnapshot = await getDocs(q);
        const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCompanies(list);
    } catch (err) { console.error(err); }
  };

  const fetchBookingFiles = async () => {
      try {
          const q = query(collection(db, 'booking_rate_files'));
          const querySnapshot = await getDocs(q);
          const files = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          files.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
          setBookingFilesList(files);
      } catch (error) { console.error("Error loading booking files:", error); }
  };

  const fetchPartyFiles = async (companyId) => {
      try {
          const q = query(collection(db, 'party_rate_uploads'), where('companyId', '==', companyId));
          const querySnapshot = await getDocs(q);
          const files = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          files.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
          setPartyFilesList(files);
      } catch (error) { console.error("Error loading party files:", error); }
  };

  // --- Helpers ---
  const generateSignature = async (params, secret) => {
    const sortedKeys = Object.keys(params).sort();
    const stringToSign = sortedKeys.map(key => `${key}=${params[key]}`).join('&') + secret;
    const encoder = new TextEncoder();
    const data = encoder.encode(stringToSign);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // --- FIX: Correct Link Logic ---
  const getDownloadUrl = (file) => {
      if (!file || !file.fileUrl) return '';

      // যাদি ফাইলটি 'raw' হয় (যেমন কিছু PDF বা Excel), অরিজিনাল লিংক রিটার্ন করো
      if (file.resource_type === 'raw') {
          return file.fileUrl;
      }

      // যদি ইমেজ বা সাধারণ PDF হয়, তাহলে ফোর্স ডাউনলোড ফ্ল্যাগ যোগ করো
      if (file.fileUrl.includes('/upload/')) {
          return file.fileUrl.replace('/upload/', '/upload/fl_attachment/');
      }
      
      return file.fileUrl;
  };

  const getFileType = (fileName) => {
      if (!fileName) return 'other';
      const lowerName = fileName.toLowerCase();
      if (lowerName.match(/\.(jpeg|jpg|png|gif|webp)$/)) return 'image';
      if (lowerName.endsWith('.pdf')) return 'pdf';
      if (lowerName.match(/\.(xlsx|xls|csv)$/)) return 'excel';
      return 'other';
  };

  // --- Upload Logic ---
  const handleUpload = async (file, folderPath, collectionName, metaData) => {
      const timestamp = Math.round((new Date()).getTime() / 1000);
      const params = { folder: folderPath, timestamp: timestamp };
      const signature = await generateSignature(params, API_SECRET);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", API_KEY);
      formData.append("timestamp", timestamp);
      formData.append("folder", folderPath);
      formData.append("signature", signature);

      const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`, {
          method: "POST", body: formData
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message);

      await addDoc(collection(db, collectionName), {
          ...metaData,
          fileUrl: data.secure_url,
          fileName: file.name,
          public_id: data.public_id,
          resource_type: data.resource_type, // Important for PDF fix
          uploadedAt: serverTimestamp()
      });
  };

  // --- Delete Logic ---
  const deleteFromCloudinary = async (public_id, resource_type) => {
        if (!public_id) return;
        const timestamp = Math.round((new Date()).getTime() / 1000);
        const params = { public_id: public_id, timestamp: timestamp };
        const signature = await generateSignature(params, API_SECRET);
        const formData = new FormData();
        formData.append("public_id", public_id);
        formData.append("api_key", API_KEY);
        formData.append("timestamp", timestamp);
        formData.append("signature", signature);
        const type = (resource_type === 'image' || resource_type === 'raw' || resource_type === 'video') ? resource_type : 'image';
        await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${type}/destroy`, { method: "POST", body: formData });
  };

  const handleDeleteFile = async (e, collectionName, fileObj, refreshFunc) => {
      e.stopPropagation();
      if(!window.confirm("Are you sure you want to delete this file?")) return;
      try {
          if (fileObj.public_id) {
              await deleteFromCloudinary(fileObj.public_id, fileObj.resource_type);
          }
          await deleteDoc(doc(db, collectionName, fileObj.id));
          setMessage("Deleted.");
          setTimeout(()=>setMessage(''), 2000);
          refreshFunc();
      } catch(e) { console.error(e); }
  }

  // --- Action Handlers ---
  const onBookingUpload = async (e) => {
    e.preventDefault();
    if (!bookingFile) { setMessage('Select file.'); return; }
    setBookingUploading(true);
    try {
        await handleUpload(bookingFile, `rates/booking`, 'booking_rate_files', { service: bookingService, date: bookingDate });
        setMessage('Uploaded!');
        setBookingFile(null);
        document.getElementById('booking-file-input').value = '';
        fetchBookingFiles();
    } catch (error) { setMessage(`Error: ${error.message}`); }
    setBookingUploading(false);
    setTimeout(()=>setMessage(''), 3000);
  };

  const onPartyUpload = async (e) => {
    e.preventDefault();
    if (!selectedCompany || !partyFile) { setMessage('Select Info.'); return; }
    setPartyUploading(true);
    try {
        const companyData = companies.find(c => c.id === selectedCompany);
        await handleUpload(partyFile, `rates/party`, 'party_rate_uploads', {
            companyId: selectedCompany,
            companyName: companyData ? companyData.name : 'Unknown',
            service: partyService,
            date: partyDate 
        });
        setMessage('Uploaded!');
        setPartyFile(null);
        document.getElementById('party-file-input').value = '';
        fetchPartyFiles(selectedCompany);
    } catch (error) { setMessage(`Error: ${error.message}`); }
    setPartyUploading(false);
    setTimeout(()=>setMessage(''), 3000);
  };

  // --- FIX: Updated Click Handler ---
  const handleFileClick = (file) => {
      const type = getFileType(file.fileName);
      
      if (type === 'image') {
          // ইমেজ হলে প্রিভিউ দেখাবে
          setPreviewImage(file);
      } else {
          // PDF বা Excel হলে নতুন ট্যাবে লিংক ওপেন হবে
          // 'getDownloadUrl' এখন সঠিক লিংক রিটার্ন করবে
          const url = getDownloadUrl(file);
          window.open(url, '_blank');
      }
  };

  // --- Styles ---
  const containerStyle = { padding: '20px', background: '#f0f2f5', minHeight: '100vh', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" };
  const sectionTitle = { borderLeft: '4px solid', paddingLeft: '10px', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold', color: 'black' }; 
  
  const gridStyle = {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: '15px',
      marginTop: '15px'
  };

  const cardStyle = {
      background: 'white',
      borderRadius: '8px',
      padding: '12px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'transform 0.2s',
      position: 'relative',
      border: '1px solid #e1e4e8'
  };

  const iconStyle = { fontSize: '28px', marginBottom: '8px', color: '#555' };
  const serviceNameStyle = { fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '4px' };
  const dateStyle = { fontSize: '11px', color: '#888' };
  const inputStyle = { width:'100%', padding:'10px', border:'1px solid #ddd', borderRadius:'4px', outline:'none' };
  
  const fileInputWrapper = {
    position: 'relative',
    border: '2px dashed #ccc',
    borderRadius: '8px',
    background: '#fafafa',
    marginTop: '15px',
    height: '100px', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    textAlign: 'center'
  };
  
  const fileInputHidden = {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      opacity: 0,
      cursor: 'pointer'
  };

  const getFileIcon = (fileName) => {
      const type = getFileType(fileName);
      if (type === 'excel') return '📊'; 
      if (type === 'pdf') return '📄';   
      if (type === 'image') return '🖼️'; 
      return '📁'; 
  };

  // SVG Trash Icon
  const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  );

  return (
    <div style={containerStyle}>
      <h2 style={{color: '#333', marginBottom: '20px', fontSize: '22px'}}>Rate Management</h2>
      <datalist id="service-options"><option value="DHL"/><option value="FedEx"/><option value="UPS"/><option value="Aramex"/></datalist>

      {/* --- BOOKING RATES --- */}
      <div style={{marginBottom: '40px'}}>
        <div style={{...sectionTitle, borderColor: '#0972d4'}}>Booking Rates (Buying)</div>
        
        <form onSubmit={onBookingUpload} style={{background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
            <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap'}}>
                <div style={{flex: 1, minWidth: '150px'}}>
                    <label style={{fontSize:'12px', fontWeight:'500', display:'block', marginBottom:'5px'}}>Service Name</label>
                    <input list="service-options" value={bookingService} onChange={e=>setBookingService(e.target.value)} style={inputStyle} placeholder="Select Service" required/>
                </div>
                <div style={{flex: 1, minWidth: '150px'}}>
                    <label style={{fontSize:'12px', fontWeight:'500', display:'block', marginBottom:'5px'}}>Effective Date</label>
                    <input type="date" value={bookingDate} onChange={e=>setBookingDate(e.target.value)} style={inputStyle} required/>
                </div>
            </div>

            <div style={fileInputWrapper}>
                <span style={{color: '#777', fontWeight: '500'}}>
                    {bookingFile ? `Selected: ${bookingFile.name}` : '+ Click or Drag File Here'}
                </span>
                <input type="file" id="booking-file-input" onChange={e=>setBookingFile(e.target.files[0])} style={fileInputHidden} required/>
            </div>

            <button type="submit" disabled={bookingUploading} style={{marginTop: '15px', padding: '12px 25px', background: '#0972d4', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight:'bold', width: '100%'}}>
                {bookingUploading ? 'Uploading...' : 'Upload Booking Rate'}
            </button>
        </form>

        <div style={gridStyle}>
            {bookingFilesList.map(file => (
                <div key={file.id} style={cardStyle} onClick={() => handleFileClick(file)} title="Click to View/Download">
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'start'}}>
                        <div style={iconStyle}>{getFileIcon(file.fileName)}</div>
                        <button onClick={(e) => handleDeleteFile(e, 'booking_rate_files', file, fetchBookingFiles)} style={{background:'#ffebee', border:'none', borderRadius:'50%', width:'30px', height:'30px', cursor:'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d32f2f'}}>
                            <TrashIcon />
                        </button>
                    </div>
                    <div>
                        <div style={serviceNameStyle}>{file.service}</div>
                        <div style={dateStyle}>{file.date}</div>
                        <div style={{fontSize:'10px', color:'#aaa', marginTop:'4px', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>{file.fileName}</div>
                    </div>
                </div>
            ))}
        </div>
        {bookingFilesList.length === 0 && <p style={{fontSize:'13px', color:'#999', marginTop:'10px'}}>No files uploaded.</p>}
      </div>

      {/* --- PARTY RATES --- */}
      <div>
        <div style={{...sectionTitle, borderColor: '#28a745'}}>Party Rates (Selling)</div>
        
        <form onSubmit={onPartyUpload} style={{background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
            <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap'}}>
                <div style={{flex: 1, minWidth: '150px'}}>
                    <label style={{fontSize:'12px', fontWeight:'500', display:'block', marginBottom:'5px'}}>Select Company</label>
                    <select value={selectedCompany} onChange={e=>setSelectedCompany(e.target.value)} style={inputStyle} required>
                        <option value="">-- Select --</option>
                        {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div style={{flex: 1, minWidth: '150px'}}>
                    <label style={{fontSize:'12px', fontWeight:'500', display:'block', marginBottom:'5px'}}>Service Name</label>
                    <input list="service-options" value={partyService} onChange={e=>setPartyService(e.target.value)} style={inputStyle} placeholder="Service" required/>
                </div>
                <div style={{flex: 1, minWidth: '150px'}}>
                    <label style={{fontSize:'12px', fontWeight:'500', display:'block', marginBottom:'5px'}}>Date</label>
                    <input type="date" value={partyDate} onChange={e=>setPartyDate(e.target.value)} style={inputStyle} required/>
                </div>
            </div>

            <div style={fileInputWrapper}>
                <span style={{color: '#777', fontWeight: '500'}}>
                    {partyFile ? `Selected: ${partyFile.name}` : '+ Click or Drag File Here'}
                </span>
                <input type="file" id="party-file-input" onChange={e=>setPartyFile(e.target.files[0])} style={fileInputHidden} required/>
            </div>

            <button type="submit" disabled={partyUploading} style={{marginTop: '15px', padding: '12px 25px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight:'bold', width: '100%'}}>
                {partyUploading ? 'Uploading...' : 'Upload Party Rate'}
            </button>
        </form>

        {selectedCompany && (
            <div style={gridStyle}>
                {partyFilesList.map(file => (
                    <div key={file.id} style={cardStyle} onClick={() => handleFileClick(file)} title="Click to View/Download">
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'start'}}>
                            <div style={iconStyle}>{getFileIcon(file.fileName)}</div>
                            <button onClick={(e) => handleDeleteFile(e, 'party_rate_uploads', file, () => fetchPartyFiles(selectedCompany))} style={{background:'#ffebee', border:'none', borderRadius:'50%', width:'30px', height:'30px', cursor:'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d32f2f'}}>
                                <TrashIcon />
                            </button>
                        </div>
                        <div>
                            <div style={{...serviceNameStyle, color: '#28a745'}}>{file.service}</div>
                            <div style={dateStyle}>{file.date}</div>
                            <div style={{fontSize:'10px', color:'#aaa', marginTop:'4px', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>{file.fileName}</div>
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>

      {/* --- IMAGE PREVIEW MODAL --- */}
      {previewImage && (
        <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}} onClick={() => setPreviewImage(null)}>
            <div style={{position: 'relative', maxWidth: '95%', maxHeight: '95%'}}>
                <img src={previewImage.fileUrl} alt="Preview" style={{maxWidth: '100%', maxHeight: '90vh', borderRadius: '4px', border: '2px solid white'}} />
                <button onClick={() => setPreviewImage(null)} style={{position: 'absolute', top: '-15px', right: '-15px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontWeight: 'bold'}}>X</button>
            </div>
        </div>
      )}

      {message && <div style={{position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', background: '#333', color: 'white', padding: '10px 20px', borderRadius: '20px', fontSize: '14px', zIndex: 1000}}>{message}</div>}
    </div>
  );
};

export default Rates;